import throwIfError from '@/lib/api/throwIfError'
import {getCurrency, isSupportedCountry} from '@/lib/locale/country'
import type {SupportedLocales} from '@/lib/locale/locale'
import {type CartItem, transformRawCartItem} from '@/models/cartModel'
import {type Order, transformOrder} from '@/models/orderModel'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import UserFriendlyError from '@/utils/errors/UserFriendlyError'
import {logError} from '@/utils/logger'

const NON_ERROR_API_CODES: string[] = ['ICA0000']

function localeUnderscoreToHyphen(locale: SupportedLocales) {
  return locale.replace('_', '-')
}

function getCurrencyFromLocale(locale: SupportedLocales) {
  const country = locale.split('_')[1]

  if (!isSupportedCountry(country)) {
    throw new UserFriendlyError('Unsupported country', new Error()).permanent()
  }

  const currency = getCurrency(country)

  if (!currency) {
    throw new UserFriendlyError('Unsupported country', new Error()).permanent()
  }

  return currency.toLowerCase()
}

export async function confirmOrder({
  locale,
  orderId,
  signal,
}: {
  orderId: Order['id']
  signal?: AbortSignal
  locale: SupportedLocales
}) {
  return call
    .post(
      `/api/checkout/v1/orders/${orderId}/confirm`,
      {},
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) {
        throw new Error(msg)
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- its safe since we have catch below
      return transformOrder(res.data.data as unknown)
    })
    .catch(async e => {
      return await getOrder({
        orderId,
        locale,
        signal,
      })
    })
}

export async function expireOrder({
  locale,
  orderId,
  signal,
}: {
  orderId: Order['id']
  signal?: AbortSignal
  locale: SupportedLocales
}): Promise<boolean> {
  return call
    .post(
      `/api/checkout/v1/orders/${orderId}/expire`,
      {},
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(() => {
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        logError(new Error('Cannot expire order', {cause: e}))
      }
      return false as const
    })
}

export async function getOrder({
  signal,
  orderId,
  locale,
}: {
  signal?: AbortSignal
  orderId: string
  locale: SupportedLocales
}): Promise<false | Order> {
  return call
    .get(`/api/checkout/v1/orders/${orderId}`, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- its safe
      return transformOrder(res.data.data as unknown)
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch order', e)
      }
      return false as const
    })
}

export async function getOrders({
  signal,
  locale,
}: {
  signal?: AbortSignal
  locale: SupportedLocales
}): Promise<Order[]> {
  return call
    .get(`/api/checkout/v1/orders`, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- intentional
      const records = res.data.data?.records

      if (Array.isArray(records)) {
        return records.map(transformOrder).filter(Boolean)
      }
      return []
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch orders', e)
      }
      return []
    })
}

export async function cancelOrder({
  signal,
  orderId,
  locale,
}: {
  signal?: AbortSignal
  orderId: string
  locale: SupportedLocales
}) {
  return call
    .patch(`/api/checkout/v1/orders/${orderId}/cancel`, undefined, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) {
        return msg
      }
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot cancel order', e)
      }
      return 'Cannot cancel order'
    })
}

export async function returnOrder({
  signal,
  orderId,
  lineItems,
  locale,
}: {
  signal?: AbortSignal
  orderId: string
  lineItems: CartItem[]
  locale: SupportedLocales
}) {
  const currency = getCurrencyFromLocale(locale)

  return call
    .patch(
      `/api/checkout/v1/orders/${orderId}/return`,
      {
        lineItems: lineItems.map(item => transformRawCartItem(item, currency)),
      },
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) {
        return msg
      }
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot return order', e)
      }
      return 'Cannot return order'
    })
}
